import { computed, ref } from 'vue';
import { http } from '@/interceptors/axiosRequestInterceptor';
import { OrderZendeskTickets, ZendeskTab, ZendeskTicket, ZendeskTicketStatus } from '@/models';

/**
 * Zendesk conversations behind an order (AB#21116 — HummelUS).
 *
 * Endpoint contract (frozen — see zendeskTickets.contract.spec.ts):
 *   GET orders/:orderId/zendesk-tickets[?fresh=1]
 *     → { success, message, result: OrderZendeskTickets }
 *   `success:false` with empty tabs means Zendesk was unreachable — the panel shows
 *   the message and a Retry, the order view is unaffected.
 *   `result.supported:false` means the order's company is not covered (button hidden).
 *
 * Read-only: the panel never writes to Zendesk; replies happen via "Open in Zendesk".
 */

export const EMPTY_RESULT = (): OrderZendeskTickets => ({
  supported: false,
  order_no: '',
  brand: null,
  customer: [],
  factory: [],
  fetched_at: '',
});

export const listUrl = (orderId: number | string, fresh = false): string =>
  `orders/${orderId}/zendesk-tickets${fresh ? '?fresh=1' : ''}`;

/**
 * Companies whose orders get the Zendesk button. Mirrors ZENDESK_HUMMELUS_COMPANY_IDS on
 * the backend; VITE_ZENDESK_COMPANY_IDS ("172,180") overrides the default (172 = Hummel Pro NA).
 * The backend still decides `supported` — this only hides the button where it would be pointless.
 */
export const parseCompanyIds = (raw: string | undefined | null): number[] =>
  String(raw ?? '')
    .split(',')
    .map((s) => Number.parseInt(s.trim(), 10))
    .filter((n) => Number.isInteger(n) && n > 0);

export const ZENDESK_COMPANY_IDS: readonly number[] = (() => {
  const fromEnv = parseCompanyIds(import.meta.env.VITE_ZENDESK_COMPANY_IDS as string | undefined);
  return fromEnv.length ? fromEnv : [172];
})();

export const isZendeskCompany = (companyId: number | null | undefined): boolean =>
  companyId !== null && companyId !== undefined && ZENDESK_COMPANY_IDS.includes(Number(companyId));

/** Tailwind classes for a Zendesk status pill (Zendesk's own colour language). */
export const statusClass = (status: ZendeskTicketStatus): string => {
  switch (status) {
    case 'new':
      return 'bg-amber-100 text-amber-800';
    case 'open':
      return 'bg-red-100 text-red-800';
    case 'pending':
      return 'bg-sky-100 text-sky-800';
    case 'hold':
      return 'bg-slate-200 text-slate-800';
    case 'solved':
    case 'closed':
      return 'bg-gray-100 text-gray-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

export const personLabel = (p: { name: string | null; email: string | null; id: number } | null): string => {
  if (!p) return '—';
  return p.name || p.email || `#${p.id}`;
};

export const formatDateTime = (value: string | null | undefined): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export const formatBytes = (bytes: number): string => {
  if (!bytes || bytes < 1024) return `${bytes || 0} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const ALLOWED_TAGS = new Set([
  'a', 'b', 'strong', 'i', 'em', 'u', 's', 'p', 'br', 'div', 'span', 'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'hr', 'img',
]);
const ALLOWED_ATTRS: Record<string, Set<string>> = {
  a: new Set(['href', 'title']),
  img: new Set(['src', 'alt', 'title', 'width', 'height']),
  td: new Set(['colspan', 'rowspan']),
  th: new Set(['colspan', 'rowspan']),
};
const SAFE_URL = /^(https?:|mailto:|tel:)/i;

/**
 * Allow-list sanitiser for Zendesk comment HTML. Zendesk already strips scripts,
 * but the admin renders this with v-html, so nothing executable, no inline styles
 * or event handlers, and only http(s)/mailto links survive. Links open in a new tab.
 */
export const sanitizeHtml = (html: string): string => {
  if (!html) return '';
  if (typeof DOMParser === 'undefined') return '';
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const walk = (node: Element) => {
    for (const child of Array.from(node.children)) {
      const tag = child.tagName.toLowerCase();
      if (!ALLOWED_TAGS.has(tag)) {
        // Drop dangerous containers wholesale, unwrap the rest.
        if (['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'svg', 'math', 'link', 'meta'].includes(tag)) {
          child.remove();
        } else {
          const frag = doc.createDocumentFragment();
          while (child.firstChild) frag.appendChild(child.firstChild);
          child.replaceWith(frag);
        }
        continue;
      }
      const allowed = ALLOWED_ATTRS[tag] ?? new Set<string>();
      for (const attr of Array.from(child.attributes)) {
        const name = attr.name.toLowerCase();
        if (!allowed.has(name)) {
          child.removeAttribute(attr.name);
          continue;
        }
        if ((name === 'href' || name === 'src') && !SAFE_URL.test(attr.value.trim())) {
          child.removeAttribute(attr.name);
        }
      }
      if (tag === 'a') {
        child.setAttribute('target', '_blank');
        child.setAttribute('rel', 'noopener noreferrer');
      }
      walk(child);
    }
  };
  walk(doc.body);
  return doc.body.innerHTML;
};

interface ApiResponse {
  success: boolean;
  message?: string;
  result: OrderZendeskTickets;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useZendeskTickets() {
  const data = ref<OrderZendeskTickets>(EMPTY_RESULT());
  const isLoading = ref(false);
  const error = ref<string | null>(null);
  const loadedFor = ref<number | string | null>(null);

  const customer = computed<ZendeskTicket[]>(() => data.value.customer ?? []);
  const factory = computed<ZendeskTicket[]>(() => data.value.factory ?? []);
  const counts = computed<Record<ZendeskTab, number>>(() => ({
    customer: data.value.total?.customer ?? customer.value.length,
    factory: data.value.total?.factory ?? factory.value.length,
  }));

  const load = async (orderId: number | string, fresh = false): Promise<OrderZendeskTickets> => {
    isLoading.value = true;
    error.value = null;
    try {
      const { data: body } = await http.get<ApiResponse>(listUrl(orderId, fresh));
      data.value = { ...EMPTY_RESULT(), ...(body.result ?? {}) };
      loadedFor.value = orderId;
      if (!body.success) error.value = body.message || 'Zendesk is not reachable right now.';
      return data.value;
    } catch {
      data.value = EMPTY_RESULT();
      loadedFor.value = orderId;
      error.value = 'Could not load Zendesk conversations.';
      return data.value;
    } finally {
      isLoading.value = false;
    }
  };

  const reset = () => {
    data.value = EMPTY_RESULT();
    error.value = null;
    loadedFor.value = null;
  };

  return { data, customer, factory, counts, isLoading, error, loadedFor, load, reset };
}
